export default class BasicSlider {

    static CURRENT_CLASS = "is-current";
    static SELF_SELECTOR = ".basic-slider";
    static SLIDES_SELECTOR = ".basic-slider__slides";
    static PREV_SELECTOR = ".basic-slider__arrow--prev";
    static NEXT_SELECTOR = ".basic-slider__arrow--next";
    static WATCH_ATTR = "data-basic-slider-watch-attr";
    static PROGRESS_PROP = "--_progress";
    static COUNT_PROP = "--_count";

    static init(): void {
        const selfEls = document.querySelectorAll<HTMLElement>(this.SELF_SELECTOR);
        selfEls.forEach(this.initSlider.bind(this));
    }

    static initSlider(self: HTMLElement): void {
        const slides: HTMLElement | null = self.querySelector(this.SLIDES_SELECTOR);
        if (!slides) return;
        const prev: HTMLElement | null = self.querySelector(this.PREV_SELECTOR);
        const next: HTMLElement | null = self.querySelector(this.NEXT_SELECTOR);

        this.setInitState(self, slides, prev, next);

        const update = () => {
            clearTimeout(self.dataset.detectCurrentDebounce);
            self.dataset.detectCurrentDebounce = String(setTimeout(() => {
                this.setCurrent(slides, this.detectCurrent(slides));
            }, 50));
            this.updateUI(self, slides, prev, next);
        };
        slides.addEventListener("scroll", update);
        slides.addEventListener("resize", update);
        this.initSlidesWatcher(self, slides, prev, next);

        if (!prev || !next) return;
        prev.addEventListener("click", () => prev.getAttribute("aria-disabled") !== "true" && this.prev(slides));
        next.addEventListener("click", () => next.getAttribute("aria-disabled") !== "true" && this.next(slides));
    }

    static initSlidesWatcher(self: HTMLElement, slides: HTMLElement, prev: HTMLElement | null, next: HTMLElement | null): void {
        const observer = new MutationObserver((mutations) => {
            if (mutations.some(m => m.addedNodes.length || m.removedNodes.length || (m.attributeName && m.target.parentElement === slides))) {
                this.setInitState(self, slides, prev, next);
            }
        });

        const watchAttr = self.getAttribute(this.WATCH_ATTR);
        observer.observe(self, { 
            attributes: Boolean(watchAttr?.length), 
            attributeFilter: watchAttr?.length ? watchAttr.split(/\s*,\s*/g): undefined, 
            subtree: true, 
            childList: true 
        });
    }

    static getShownSlideElements(slides: HTMLElement): HTMLElement[] {
        return (Array.from(slides.children) as HTMLElement[]).filter(slide => {
            return getComputedStyle(slide).display !== "none";
        });
    }

    static setInitState(self: HTMLElement, slides: HTMLElement, prev: HTMLElement | null, next: HTMLElement | null): void {
        const slideEls = this.getShownSlideElements(slides);
        self.style.setProperty(this.COUNT_PROP, String(slideEls.length));
        if (slideEls[0]) {
            this.scrollTo(slides, slideEls[0]);
        }
        this.updateUI(self, slides, prev, next);
    }

    static detectCurrent(slides: HTMLElement): HTMLElement | null {
        const slideEls = this.getShownSlideElements(slides);
        const slidesRect = slides.getBoundingClientRect();
        let currentSlide: HTMLElement | null = null;
        
        let minDist = Infinity;
        for (let i = 0; i < slideEls.length; i++) {
            const rect = slideEls[i].getBoundingClientRect();
            if (Math.abs(slidesRect.left - rect.left) < minDist) {
                minDist = Math.abs(slidesRect.left - rect.left);
                currentSlide = slideEls[i];
            }
        }

        return currentSlide;
    }

    static setCurrent(slides: HTMLElement, currentSlide?: HTMLElement | null): void {
        for (let i = 0; i < slides.children.length; i++) {
            slides.children[i].classList.remove(this.CURRENT_CLASS);
        }
        currentSlide?.classList.add(this.CURRENT_CLASS);
    }

    static next(slides: HTMLElement): void {
        const slideEls = this.getShownSlideElements(slides);
        for (let i = 0; i < slideEls.length; i++) {
            if (slideEls[i].classList.contains(this.CURRENT_CLASS)) {
                return this.scrollTo(slides, slideEls[i + 1] || slideEls[i]);
            }
        }
        if (slideEls[0]) {
            return this.scrollTo(slides, slideEls[slideEls.length - 1]);
        }
  }

    static prev(slides: HTMLElement): void {
        const slideEls = this.getShownSlideElements(slides);
        for (let i = 0; i < slideEls.length; i++) {
            if (slideEls[i].classList.contains(this.CURRENT_CLASS)) {
                return this.scrollTo(slides, slideEls[i - 1] || slideEls[i]);
            }
        }
        if (slideEls[0]) {
            return this.scrollTo(slides, slideEls[0]);
        }
    }

    static scrollTo(slides: HTMLElement, slide: HTMLElement): void {
        const slidesRect = slides.getBoundingClientRect();
        const slideRect = slide.getBoundingClientRect();
        const scrollTo = slides.scrollLeft + (slideRect.left - slidesRect.left);

        this.setCurrent(slides, slide);
        slides.scrollTo({ left: scrollTo, behavior: "smooth" });
    }

    static updateUI(self: HTMLElement, slides: HTMLElement, prev: HTMLElement | null, next: HTMLElement | null): void {
        const isScrollable = slides.scrollWidth !== slides.offsetWidth;
        const progress = isScrollable ? (slides.scrollLeft / (slides.scrollWidth - slides.offsetWidth)): 1;
        self.style.setProperty(this.PROGRESS_PROP, progress.toFixed(3));
        prev?.setAttribute("aria-disabled", String(progress <= 0 || !isScrollable));
        next?.setAttribute("aria-disabled", String(progress >= 1 || !isScrollable));
    }
}
